import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { isValidPhoneNumber, type CountryCode } from 'libphonenumber-js/mobile';
import { RegionService } from '../../services/region.service';
import { PHONE_COUNTRIES, PREFERRED_COUNTRY_CODES, PhoneCountry } from './phone-countries';

interface CountrySections {
  preferred: PhoneCountry[];
  other: PhoneCountry[];
}

@Component({
  selector: 'app-phone-input',
  standalone: true,
  imports: [CommonModule, CdkOverlayOrigin, CdkConnectedOverlay],
  templateUrl: './phone-input.html',
  styleUrl: './phone-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
})
export class PhoneInputComponent implements ControlValueAccessor, Validator {
  private readonly regionService = inject(RegionService);

  @ViewChild('searchInput') private searchInputEl?: ElementRef<HTMLInputElement>;
  @ViewChild('phoneInput') private phoneInputEl?: ElementRef<HTMLInputElement>;

  readonly selectedCountry = signal<PhoneCountry>(this.resolveDefaultCountry());
  readonly localNumber = signal('');
  readonly isOpen = signal(false);
  readonly isDisabled = signal(false);
  readonly searchQuery = signal('');
  readonly hasError = input(false);
  readonly triggerWidth = signal(0);

  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  readonly countrySections = computed<CountrySections>(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const preferredSet = new Set(PREFERRED_COUNTRY_CODES);

    if (query) {
      const matches = PHONE_COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.dialCode.includes(query) ||
          c.code.toLowerCase().includes(query),
      );
      return { preferred: [], other: matches };
    }

    return {
      preferred: PREFERRED_COUNTRY_CODES.map((code) =>
        PHONE_COUNTRIES.find((c) => c.code === code),
      ).filter((c): c is PhoneCountry => !!c),
      other: PHONE_COUNTRIES.filter((c) => !preferredSet.has(c.code)),
    };
  });

  private onChange: (value: string) => void = () => {};
  private onTouchedFn: () => void = () => {};
  private validatorOnChange: () => void = () => {};

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen()) {
      this.closeDropdown();
    }
  }

  writeValue(value: string | null): void {
    if (!value) {
      this.localNumber.set('');
      return;
    }

    const cleaned = value.replace(/\s/g, '');
    if (cleaned.startsWith('+')) {
      let bestMatch: PhoneCountry | null = null;
      for (const country of PHONE_COUNTRIES) {
        const dc = country.dialCode.replace(/\s/g, '');
        if (cleaned.startsWith(dc)) {
          if (!bestMatch || dc.length > bestMatch.dialCode.replace(/\s/g, '').length) {
            bestMatch = country;
          }
        }
      }
      if (bestMatch) {
        this.selectedCountry.set(bestMatch);
        const dc = bestMatch.dialCode.replace(/\s/g, '');
        this.localNumber.set(cleaned.slice(dc.length));
        return;
      }
    }

    this.localNumber.set(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    const digits = this.localNumber().replace(/\D/g, '');
    if (!digits) return null;

    const fullNumber = `${this.selectedCountry().dialCode}${digits}`;
    const countryCode = this.selectedCountry().code as CountryCode;

    if (!isValidPhoneNumber(fullNumber, countryCode)) {
      return { validatePhoneNumber: true };
    }

    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorOnChange = fn;
  }

  protected toggleDropdown(trigger: CdkOverlayOrigin): void {
    if (this.isDisabled()) return;
    this.isOpen.update((v) => !v);
    if (this.isOpen()) {
      this.searchQuery.set('');
      this.triggerWidth.set(trigger.elementRef.nativeElement.offsetWidth);
      requestAnimationFrame(() => {
        this.searchInputEl?.nativeElement.focus();
      });
    }
  }

  protected closeDropdown(): void {
    this.isOpen.set(false);
    this.searchQuery.set('');
  }

  protected selectCountry(country: PhoneCountry): void {
    this.selectedCountry.set(country);
    this.closeDropdown();
    this.emitValue();
    requestAnimationFrame(() => {
      this.phoneInputEl?.nativeElement.focus();
    });
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.localNumber.set(value);
    this.emitValue();
  }

  protected onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  protected onBlur(): void {
    this.onTouchedFn();
  }

  private emitValue(): void {
    const digits = this.localNumber().replace(/\D/g, '');
    if (!digits) {
      this.onChange('');
    } else {
      this.onChange(`${this.selectedCountry().dialCode}${digits}`);
    }
    this.validatorOnChange();
  }

  private resolveDefaultCountry(): PhoneCountry {
    const match = PHONE_COUNTRIES.find((c) => c.code === this.regionService.country);
    return match ?? PHONE_COUNTRIES.find((c) => c.code === 'US')!;
  }
}
